import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { products } from '../data/products';
import { savePartialOrder, saveConfirmedOrder } from '../services/sheetService';

const OrderSection = () => {
  const navigate = useNavigate();
  const [selectedProductId, setSelectedProductId] = useState(products[1].id);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedProduct = products.find(p => p.id === selectedProductId);

  // Partial save logic with debounce
  useEffect(() => {
    // BUG FIX 3: Do not trigger partial save if order is currently being placed
    if (isSubmitting) return;

    const handler = setTimeout(() => {
      // BUG FIX 1: Include precise flattened product structure during partial save
      if (formData.name.trim() !== '' || formData.phone.trim() !== '') {
        savePartialOrder({ 
          ...formData, 
          product: selectedProduct.name,
          price: selectedProduct.price,
          freeItems: selectedProduct.freeItems
        });
      }
    }, 3000);

    return () => clearTimeout(handler);
  }, [formData, selectedProduct, isSubmitting]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('অনুগ্রহ করে আপনার নাম দিন');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('অনুগ্রহ করে আপনার ফোন নম্বর দিন');
      return;
    }
    
    setIsSubmitting(true);
    
    // BUG FIX 2: Flattened DB Payload Structure for AppScript compatibility
    const dbPayload = {
      ...formData,
      product: selectedProduct.name,
      price: selectedProduct.price,
      freeItems: selectedProduct.freeItems,
      orderTime: new Date().toISOString()
    };

    // Keep nested structure exclusively for the Success UI Page presentation
    const uiOrderData = {
      customer: formData,
      product: selectedProduct,
      orderTime: dbPayload.orderTime
    };

    try {
      await saveConfirmedOrder(dbPayload);
      toast.success('অর্ডার সম্পন্ন হয়েছে!');
      navigate('/success', { state: { orderDetails: uiOrderData } });
    } catch (error) {
      toast.error('অর্ডার সেভ করতে সমস্যা হয়েছে, তবে আমরা আপনার অর্ডারটি রেকর্ড করেছি।');
      navigate('/success', { state: { orderDetails: uiOrderData } });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="order" className="bg-gray-50 py-16 px-4">
      <div className="max-w-5xl mx-auto">
        
        {/* Product Selector */}
        <div className="bg-white shadow-sm border border-gray-100 rounded-2xl p-4 md:p-6 mb-8">
          <div className="flex flex-col gap-3">
            {products.map((product) => {
              const isSelected = selectedProductId === product.id;
              return (
                <div 
                  key={product.id}
                  onClick={() => setSelectedProductId(product.id)}
                  className={`flex flex-col sm:flex-row items-center gap-4 transition-all ${
                    isSelected 
                      ? 'border-2 border-green-500 rounded-xl p-4 bg-green-50' 
                      : 'border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-green-400'
                  }`}
                >
                  <div className="shrink-0 relative">
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="w-20 h-20 rounded-xl object-cover bg-white" 
                    />
                    {product.badge && (
                      <span className="absolute -top-3 -left-2 bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full z-10 whitespace-nowrap">
                        {product.badge}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex-grow text-center sm:text-left">
                    <h3 className="font-semibold text-gray-800 text-base">
                      {product.name}
                    </h3>
                    <div className="mt-1">
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full inline-block">
                        {product.freeItems}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-row sm:flex-col items-center justify-between sm:justify-center w-full sm:w-auto gap-4 sm:gap-2 shrink-0">
                    <span className="font-bold text-green-600 text-lg w-20 text-right sm:text-center">৳{product.price}</span>
                    <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center shrink-0 border ${isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300 bg-white'}`}>
                      {isSelected && <span className="w-2 h-2 md:w-2.5 md:h-2.5 bg-white rounded-full"></span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form and Summary Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          
          {/* Left Side — Billing Form */}
          <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">বিলিং তথ্য</h3>
            
            <form onSubmit={handlePlaceOrder} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">
                  আপনার নাম <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="আপনার সম্পূর্ণ নাম লিখুন" 
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">
                  ফোন নম্বর <span className="text-red-500">*</span>
                </label>
                <input 
                  type="tel" 
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="১১ ডিজিটের মোবাইল নম্বর" 
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600 mb-1 block">
                  সম্পূর্ণ ঠিকানা
                </label>
                <textarea 
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="বাসা, রোড, এলাকা" 
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all resize-y min-h-[100px]"
                />
                <p className="text-xs text-gray-400 mt-1">ঐচ্ছিক — না দিলেও অর্ডার হবে</p>
              </div>
            </form>
          </div>

          {/* Right Side — Order Summary */}
          <div className="lg:col-span-5 relative">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 lg:sticky lg:top-8">
              <h3 className="text-lg font-bold text-gray-800 mb-4">অর্ডার সারাংশ</h3>
              
              <div className="flex items-center gap-4 mb-3">
                <img src={selectedProduct.image} alt={selectedProduct.name} className="w-16 h-16 rounded-xl object-cover shrink-0 border border-gray-100" />
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm leading-tight">{selectedProduct.name}</h4>
                  <p className="text-green-600 text-xs font-medium mt-1">{selectedProduct.freeItems}</p>
                </div>
              </div>

              <div className="border-t border-gray-100 my-3"></div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>প্রোডাক্ট মূল্য</span>
                  <span className="font-medium text-gray-800">৳{selectedProduct.price}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>ডেলিভারি চার্জ</span>
                  <span className="font-medium text-green-600">সম্পূর্ণ ফ্রি 🎉</span>
                </div>
              </div>

              <div className="border-t border-gray-100 my-3"></div>

              <div className="flex justify-between items-center text-base font-bold text-gray-800 mb-3">
                <span>সর্বমোট</span>
                <span className="text-green-600">৳{selectedProduct.price}</span>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-[13px] sm:text-sm text-gray-600 flex items-start sm:items-center gap-2 mt-3 mb-4">
                <span className="shrink-0 text-lg mt-0.5 sm:mt-0">💵</span>
                <span>পণ্য হাতে পেয়ে মূল্য পরিশোধ করুন (Cash on Delivery)</span>
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={isSubmitting}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold text-base sm:text-lg py-4 rounded-xl mt-4 transition-all shadow-md disabled:opacity-70 disabled:hover:bg-green-600 active:scale-95 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <span className="animate-pulse">প্রসেস হচ্ছে...</span>
                ) : (
                  <>🔒 অর্ডার কনফার্ম করুন — ৳{selectedProduct.price}</>
                )}
              </button>
            </div>
          </div>
          
        </div>

      </div>
    </section>
  );
};

export default OrderSection;
